import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ArrowUpRight,
  Lightbulb,
  MessageSquare,
  MoreHorizontal,
  Plus,
} from 'lucide-react';

import { listDepartments } from '@/api/employees.api';
import {
  createIssue,
  listIssues,
  promoteIssue,
  rankIssues,
  updateIssue,
  type Issue,
  type IssueStatus,
  type IssueTerm,
} from '@/api/eos/issues.api';
import type { RockType } from '@/api/eos/rocks.api';
import { CommentThread } from '@/components/comms/CommentThread';
import { QuarterPicker } from '@/components/eos/QuarterPicker';
import { Badge, type BadgeProps } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Tabs } from '@/components/ui/Tabs';
import { describeEosError } from '@/lib/eos/errors';
import { quarterStartOf, toIsoDate } from '@/lib/eos/quarters';
import { hasCapability, isSuperadmin } from '@/lib/capabilities';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';

/**
 * EOS Issues — Stream B Wave 1 (ADR-023).
 *
 * The IDS list. Any member raises and updates issues (Identify-Discuss-
 * Solve happens out loud); ranking is gated to facilitators
 * (run_meetings), superadmins, and department leads — client-side
 * VISIBILITY only, the server enforces the real rule. Promotion turns
 * an issue into a rock (quarter-sized) or a to-do (week-sized).
 */

const SELECT_CLS =
  'rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20';

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

const STATUS_META: Record<IssueStatus, { label: string; variant: BadgeProps['variant'] }> = {
  identified: { label: 'Identified', variant: 'neutral' },
  discussing: { label: 'Discussing', variant: 'info' },
  solved: { label: 'Solved', variant: 'success' },
  dropped: { label: 'Dropped', variant: 'warning' },
};

/** identified → discussing → solved → identified (dropped is terminal, set elsewhere). */
const NEXT_STATUS: Record<IssueStatus, IssueStatus> = {
  identified: 'discussing',
  discussing: 'solved',
  solved: 'identified',
  dropped: 'identified',
};

export function IssuesPage() {
  const { state: authState } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();

  const me = authState.status === 'authenticated' ? authState.me : null;
  // Dept leads may rank only DEPARTMENT lists — the org/leadership list
  // ('' → null server-side) always 403s them, so no dead controls there
  // (wave-1 review fix). The server enforces own-department regardless.
  const canRank = (selectedDepartmentId: string): boolean =>
    me !== null &&
    (isSuperadmin(me) ||
      hasCapability(me, 'run_meetings') ||
      (me.membership.role === 'department_lead' && selectedDepartmentId !== ''));

  // '' = the org/leadership list (departmentId null server-side).
  const [departmentId, setDepartmentId] = useState('');
  const [term, setTerm] = useState<IssueTerm>('short_term');
  const [raiseOpen, setRaiseOpen] = useState(false);
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [solving, setSolving] = useState<Issue | null>(null);
  const [promoting, setPromoting] = useState<{ issue: Issue; to: 'rock' | 'todo' } | null>(null);
  const [discussing, setDiscussing] = useState<Issue | null>(null);

  const departmentsQuery = useQuery({ queryKey: ['departments'], queryFn: listDepartments });

  // The server's list has no "departmentId IS NULL" filter, so fetch the
  // term's issues org-wide and slice the selected list client-side.
  const issuesQuery = useQuery({
    queryKey: ['eos', 'issues', term],
    queryFn: () => listIssues({ term }),
  });

  const issues = useMemo(
    () =>
      (issuesQuery.data ?? []).filter((i) =>
        departmentId === '' ? i.departmentId === null : i.departmentId === departmentId,
      ),
    [issuesQuery.data, departmentId],
  );
  const openIssues = useMemo(
    () => issues.filter((i) => i.status === 'identified' || i.status === 'discussing'),
    [issues],
  );
  const closedIssues = useMemo(
    () => issues.filter((i) => i.status === 'solved' || i.status === 'dropped'),
    [issues],
  );

  function invalidate(): void {
    void queryClient.invalidateQueries({ queryKey: ['eos', 'issues'] });
  }

  const statusMutation = useMutation({
    mutationFn: ({
      id,
      status,
      resolutionNotes,
    }: {
      id: string;
      status: IssueStatus;
      resolutionNotes?: string;
    }) => updateIssue(id, { status, ...(resolutionNotes !== undefined ? { resolutionNotes } : {}) }),
    onSuccess: () => {
      invalidate();
      setSolving(null);
    },
    onError: (err) => {
      toast.show(describeEosError(err, 'Could not update the issue.'), { variant: 'danger' });
    },
  });

  const rankMutation = useMutation({
    mutationFn: (orderedIds: string[]) =>
      rankIssues({ departmentId: departmentId === '' ? null : departmentId, orderedIds }),
    onSuccess: invalidate,
    onError: (err) => {
      toast.show(describeEosError(err, 'Could not reorder the list.'), { variant: 'danger' });
      invalidate();
    },
  });

  function move(index: number, delta: -1 | 1): void {
    const target = index + delta;
    if (target < 0 || target >= openIssues.length) return;
    const ids = openIssues.map((i) => i.id);
    const tmp = ids[index];
    const other = ids[target];
    if (tmp === undefined || other === undefined) return;
    ids[index] = other;
    ids[target] = tmp;
    rankMutation.mutate(ids);
  }

  function cycleStatus(issue: Issue): void {
    const next = NEXT_STATUS[issue.status];
    if (next === 'solved') {
      // Solving prompts for optional resolution notes first.
      setSolving(issue);
      return;
    }
    statusMutation.mutate({ id: issue.id, status: next });
  }

  function renderIssue(issue: Issue, index: number, isOpen: boolean) {
    const meta = STATUS_META[issue.status];
    return (
      <li key={issue.id} className="flex items-start gap-3 px-4 py-3">
        {canRank(departmentId) && isOpen ? (
          <div className="flex shrink-0 flex-col">
            <button
              type="button"
              aria-label={`Move ${issue.title} up`}
              disabled={index === 0 || rankMutation.isPending}
              onClick={() => {
                move(index, -1);
              }}
              className="rounded p-0.5 text-kw-muted hover:bg-kw-bg hover:text-kw-shark disabled:opacity-30"
            >
              <ArrowUp className="size-3.5" />
            </button>
            <button
              type="button"
              aria-label={`Move ${issue.title} down`}
              disabled={index === openIssues.length - 1 || rankMutation.isPending}
              onClick={() => {
                move(index, 1);
              }}
              className="rounded p-0.5 text-kw-muted hover:bg-kw-bg hover:text-kw-shark disabled:opacity-30"
            >
              <ArrowDown className="size-3.5" />
            </button>
          </div>
        ) : null}
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-medium text-kw-shark">{issue.title}</p>
          {issue.description ? <p className="mt-0.5 text-[13px] text-kw-muted">{issue.description}</p> : null}
          {issue.resolutionNotes ? (
            <p className="mt-0.5 text-[12px] italic text-kw-muted">Resolution: {issue.resolutionNotes}</p>
          ) : null}
          <div className="mt-1 flex flex-wrap items-center gap-2 text-[12px] text-kw-muted">
            {issue.promotedToRockId ? <Badge variant="info">→ Rock</Badge> : null}
            {issue.promotedToTodoId ? <Badge variant="info">→ To-do</Badge> : null}
          </div>
        </div>
        <div className="relative flex shrink-0 items-center gap-1.5">
          <button
            type="button"
            aria-label={`Change status of ${issue.title} (now ${meta.label})`}
            title="Click to cycle status"
            onClick={() => {
              cycleStatus(issue);
            }}
            className="rounded-kw-full transition-opacity hover:opacity-80"
          >
            <Badge variant={meta.variant}>{meta.label}</Badge>
          </button>
          <button
            type="button"
            aria-label={`More actions for ${issue.title}`}
            onClick={() => {
              setMenuOpenId((v) => (v === issue.id ? null : issue.id));
            }}
            className="rounded p-1 text-kw-muted hover:bg-kw-bg hover:text-kw-shark"
          >
            <MoreHorizontal className="size-4" />
          </button>
          {menuOpenId === issue.id ? (
            <div className="absolute right-0 top-8 z-20 w-44 rounded-kw-md border border-kw-border bg-white py-1 shadow-kw-md">
              <button
                type="button"
                onClick={() => {
                  setMenuOpenId(null);
                  setDiscussing(issue);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] text-kw-shark hover:bg-kw-bg"
              >
                <MessageSquare className="size-3.5" />
                Discuss
              </button>
              <button
                type="button"
                disabled={issue.promotedToRockId !== null}
                onClick={() => {
                  setMenuOpenId(null);
                  setPromoting({ issue, to: 'rock' });
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] text-kw-shark hover:bg-kw-bg disabled:opacity-40"
              >
                <ArrowUpRight className="size-3.5" />
                Promote to Rock
              </button>
              <button
                type="button"
                disabled={issue.promotedToTodoId !== null}
                onClick={() => {
                  setMenuOpenId(null);
                  setPromoting({ issue, to: 'todo' });
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] text-kw-shark hover:bg-kw-bg disabled:opacity-40"
              >
                <ArrowUpRight className="size-3.5" />
                Promote to To-do
              </button>
            </div>
          ) : null}
        </div>
      </li>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="typo-h1">Issues</h1>
          <p className="mt-1 text-[14px] text-kw-muted">
            The IDS list — identify, discuss, solve. Top of the list gets discussed first.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            aria-label="Issue list"
            value={departmentId}
            onChange={(e) => {
              setDepartmentId(e.target.value);
            }}
            className={SELECT_CLS}
          >
            <option value="">Leadership / org</option>
            {(departmentsQuery.data ?? []).map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
          <Button
            onClick={() => {
              setRaiseOpen(true);
            }}
          >
            <Plus className="size-4" />
            Raise issue
          </Button>
        </div>
      </div>

      <Tabs
        tabs={[
          { id: 'short_term', label: 'Short-term' },
          { id: 'long_term', label: 'Long-term' },
        ]}
        activeTab={term}
        onChange={(id) => {
          setTerm(id as IssueTerm);
        }}
      />

      {issuesQuery.isLoading ? (
        <div className="flex justify-center py-16">
          <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
        </div>
      ) : issuesQuery.error ? (
        <Card className="flex items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
          <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
          <div>
            <p className="text-[14px] font-medium text-kw-danger-text">Couldn&apos;t load issues</p>
            <p className="mt-1 text-[13px] text-kw-danger-text/80">
              {issuesQuery.error instanceof Error ? issuesQuery.error.message : 'Unknown error'}
            </p>
          </div>
        </Card>
      ) : issues.length === 0 ? (
        <Card className="flex flex-col items-center gap-3 p-12 text-center">
          <Lightbulb className="size-10 text-kw-ghost" />
          <div>
            <p className="text-[15px] font-medium text-kw-shark">No issues on this list</p>
            <p className="mt-1 text-[13px] text-kw-muted">
              Raise one — surfacing issues is a health sign, not a failure.
            </p>
          </div>
          <Button
            onClick={() => {
              setRaiseOpen(true);
            }}
          >
            <Plus className="size-4" />
            Raise an issue
          </Button>
        </Card>
      ) : (
        <div className="space-y-4">
          <Card className="p-0">
            {openIssues.length === 0 ? (
              <p className="px-4 py-6 text-center text-[13px] text-kw-muted">Nothing open — all solved.</p>
            ) : (
              <ul className="divide-y divide-kw-border">
                {openIssues.map((issue, index) => renderIssue(issue, index, true))}
              </ul>
            )}
          </Card>

          {closedIssues.length > 0 ? (
            <div className="space-y-2">
              <h2 className="text-[13px] font-medium uppercase tracking-wide text-kw-muted">Resolved</h2>
              <Card className="p-0">
                <ul className="divide-y divide-kw-border">
                  {closedIssues.map((issue, index) => renderIssue(issue, index, false))}
                </ul>
              </Card>
            </div>
          ) : null}
        </div>
      )}

      <RaiseIssueModal
        key={`${departmentId}|${term}`}
        open={raiseOpen}
        onClose={() => {
          setRaiseOpen(false);
        }}
        onCreated={() => {
          invalidate();
          toast.show('Issue raised', { variant: 'success' });
        }}
        defaultDepartmentId={departmentId}
        defaultTerm={term}
      />

      {/* Solve modal — optional resolution notes before marking solved. */}
      <Modal
        open={solving !== null}
        onClose={() => {
          setSolving(null);
        }}
        title="Mark solved"
        size="sm"
      >
        {solving ? (
          <SolveForm
            issue={solving}
            isPending={statusMutation.isPending}
            onSubmit={(resolutionNotes) => {
              statusMutation.mutate({
                id: solving.id,
                status: 'solved',
                ...(resolutionNotes ? { resolutionNotes } : {}),
              });
            }}
            onCancel={() => {
              setSolving(null);
            }}
          />
        ) : null}
      </Modal>

      <PromoteModal
        promoting={promoting}
        onClose={() => {
          setPromoting(null);
        }}
        onPromoted={(to) => {
          invalidate();
          toast.show(to === 'rock' ? 'Promoted to a rock' : 'Promoted to a to-do', { variant: 'success' });
        }}
      />

      {/* Discussion — comment thread on the issue (Stream E, ADR-027). */}
      <Modal
        open={discussing !== null}
        onClose={() => {
          setDiscussing(null);
        }}
        title={discussing ? discussing.title : 'Discussion'}
      >
        {discussing ? <CommentThread entityType="issue" entityId={discussing.id} /> : null}
      </Modal>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Solve form (small — lives inside the solve modal)
// ─────────────────────────────────────────────────────────────────────────

function SolveForm({
  issue,
  isPending,
  onSubmit,
  onCancel,
}: {
  issue: Issue;
  isPending: boolean;
  onSubmit: (resolutionNotes: string) => void;
  onCancel: () => void;
}) {
  const [notes, setNotes] = useState('');
  return (
    <form
      onSubmit={(e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        onSubmit(notes.trim());
      }}
      className="space-y-4"
    >
      <p className="text-[13px] text-kw-shark">
        Mark <strong>{issue.title}</strong> as solved?
      </p>
      <label className="flex flex-col gap-1">
        <span className="text-[13px] font-medium text-kw-label">Resolution notes (optional)</span>
        <textarea
          value={notes}
          onChange={(e) => {
            setNotes(e.target.value);
          }}
          rows={3}
          placeholder="What was decided?"
          className="rounded-kw-md border border-kw-border bg-white px-3.5 py-2.5 text-[14px] text-kw-shark placeholder:text-kw-muted focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
        />
      </label>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" isLoading={isPending}>
          Mark solved
        </Button>
      </div>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Promote modal (→ rock: quarter + type; → to-do: due date)
// ─────────────────────────────────────────────────────────────────────────

function PromoteModal({
  promoting,
  onClose,
  onPromoted,
}: {
  promoting: { issue: Issue; to: 'rock' | 'todo' } | null;
  onClose: () => void;
  onPromoted: (to: 'rock' | 'todo') => void;
}) {
  const [quarter, setQuarter] = useState(() => toIsoDate(quarterStartOf(new Date())));
  const [rockType, setRockType] = useState<RockType>('individual');
  const [dueDate, setDueDate] = useState(() => toIsoDate(new Date(Date.now() + SEVEN_DAYS_MS)));

  const mutation = useMutation({
    mutationFn: () => {
      if (!promoting) return Promise.reject(new Error('Nothing to promote'));
      return promoting.to === 'rock'
        ? promoteIssue(promoting.issue.id, { to: 'rock', quarterStart: quarter, type: rockType })
        : promoteIssue(promoting.issue.id, {
            to: 'todo',
            dueDate: dueDate ? `${dueDate}T00:00:00.000Z` : undefined,
          });
    },
    onSuccess: () => {
      if (promoting) onPromoted(promoting.to);
      onClose();
    },
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    mutation.mutate();
  }

  return (
    <Modal
      open={promoting !== null}
      onClose={onClose}
      title={promoting?.to === 'rock' ? 'Promote to rock' : 'Promote to to-do'}
      size="sm"
    >
      {promoting ? (
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-[13px] text-kw-muted">
            <strong className="text-kw-shark">{promoting.issue.title}</strong> becomes a{' '}
            {promoting.to === 'rock' ? 'quarterly rock' : '7-day to-do'} and keeps a link back to this issue.
          </p>

          {promoting.to === 'rock' ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-1">
                <span className="text-[13px] font-medium text-kw-label">Quarter</span>
                <QuarterPicker value={quarter} onChange={setQuarter} />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[13px] font-medium text-kw-label">Type</span>
                <select
                  value={rockType}
                  onChange={(e) => {
                    setRockType(e.target.value as RockType);
                  }}
                  className={SELECT_CLS}
                >
                  <option value="individual">Individual</option>
                  <option value="department">Department</option>
                  <option value="company">Company</option>
                </select>
              </label>
            </div>
          ) : (
            <Input
              label="Due date"
              name="promote-due"
              type="date"
              value={dueDate}
              onChange={(e) => {
                setDueDate(e.target.value);
              }}
              hint="Defaults to a week out."
            />
          )}

          {mutation.error ? (
            <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
              <p className="text-[12px] text-kw-danger-text">
                {describeEosError(mutation.error, 'Could not promote the issue.')}
              </p>
            </div>
          ) : null}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" isLoading={mutation.isPending}>
              Promote
            </Button>
          </div>
        </form>
      ) : null}
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Raise issue modal
// ─────────────────────────────────────────────────────────────────────────

function RaiseIssueModal({
  open,
  onClose,
  onCreated,
  defaultDepartmentId,
  defaultTerm,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  defaultDepartmentId: string;
  defaultTerm: IssueTerm;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [departmentId, setDepartmentId] = useState(defaultDepartmentId);
  const [term, setTerm] = useState<IssueTerm>(defaultTerm);

  const departmentsQuery = useQuery({
    queryKey: ['departments'],
    queryFn: listDepartments,
    enabled: open,
  });

  const mutation = useMutation({
    mutationFn: () =>
      createIssue({
        title: title.trim(),
        description: description.trim() || undefined,
        departmentId: departmentId || undefined,
        term,
      }),
    onSuccess: () => {
      onCreated();
      onClose();
      setTitle('');
      setDescription('');
    },
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!title.trim()) return;
    mutation.mutate();
  }

  return (
    <Modal open={open} onClose={onClose} title="Raise an issue" size="sm">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Title"
          name="issue-title"
          required
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
          }}
          placeholder="e.g. Shipping errors are trending up"
        />

        <label className="flex flex-col gap-1">
          <span className="text-[13px] font-medium text-kw-label">Description</span>
          <textarea
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
            }}
            rows={2}
            placeholder="Optional context"
            className="rounded-kw-md border border-kw-border bg-white px-3.5 py-2.5 text-[14px] text-kw-shark placeholder:text-kw-muted focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
          />
        </label>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1">
            <span className="text-[13px] font-medium text-kw-label">List</span>
            <select
              value={departmentId}
              onChange={(e) => {
                setDepartmentId(e.target.value);
              }}
              className={SELECT_CLS}
            >
              <option value="">Leadership / org</option>
              {(departmentsQuery.data ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[13px] font-medium text-kw-label">Term</span>
            <select
              value={term}
              onChange={(e) => {
                setTerm(e.target.value as IssueTerm);
              }}
              className={SELECT_CLS}
            >
              <option value="short_term">Short-term (weekly L10)</option>
              <option value="long_term">Long-term (V/TO)</option>
            </select>
          </label>
        </div>

        {mutation.error ? (
          <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
            <p className="text-[12px] text-kw-danger-text">
              {describeEosError(mutation.error, 'Could not raise the issue.')}
            </p>
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={mutation.isPending} disabled={!title.trim()}>
            Raise issue
          </Button>
        </div>
      </form>
    </Modal>
  );
}
